/**
 * useChatConfig Hook
 * Standardized API hooks for chat configuration management
 *
 * Refactored: Phase 3.6 - Factory Pattern Migration
 * Pattern: Uses createQueryHookNoParams and createMutationHook factories for code reduction
 * Previous: Manual useQuery/useMutation with createApiClient()
 * Current: Factory-based hooks with built-in logging and invalidation
 *
 * Migration Benefits:
 * - Reduced code duplication
 * - Standardized logging patterns
 * - Centralized query invalidation
 * - Type-safe configuration
 * - Automatic API client injection
 */

import { ChatConfigResponse, ChatConfigRequest, ChatProviderResponse, StatusResponse } from '@/api/ApiClient';
import { log } from '@/utils/errors/logger';
import { QUERY_STALE_TIME } from '@/constants/queryConfig';
import { createQueryHookNoParams, createMutationHook } from '@/hooks/api/factories';
import { usePermissions } from '@/hooks/permissions/usePermissions';

// ============================================================================
// Query Keys
// ============================================================================

export const chatConfigKeys = {
  all: ['chatConfigs'] as const,
  configs: () => [...chatConfigKeys.all, 'configs'] as const,
  current: () => [...chatConfigKeys.all, 'current'] as const,
  providers: () => [...chatConfigKeys.all, 'providers'] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to get all chat configurations.
 * Only enabled for users with chat model configuration permission.
 *
 * Features:
 * - LONG stale time (5m) as chat configs change infrequently
 * - Conditional query enabled based on permissions
 * - Automatic request/response/error logging via logContext
 *
 * Usage:
 * @example
 * const { data: configs } = useGetChatConfigs();
 */
const useGetChatConfigsBase = createQueryHookNoParams<ChatConfigResponse[]>({
  queryKey: () => chatConfigKeys.configs(),
  queryFn: (apiClient) =>
    apiClient.getAllConfigs().then(response => {
      log.api.response('GET', '/api/admin/chat-config/all', 200, { count: response.data.length });
      return response.data;
    }),
  logContext: 'GET /api/admin/chat-config/all',
  staleTime: QUERY_STALE_TIME.LONG, // Chat configs change infrequently
});

export function useGetChatConfigs() {
  const { can } = usePermissions();
  return useGetChatConfigsBase({
    enabled: can.configureChatModels
  });
}

/**
 * Hook to get current active chat configuration.
 * Only enabled for users with chat model configuration permission.
 *
 * Features:
 * - MEDIUM stale time (1m) as current config may change
 * - Conditional query enabled based on permissions
 * - Automatic request/response/error logging via logContext
 *
 * Usage:
 * @example
 * const { data: currentConfig } = useGetCurrentChatConfig();
 */
const useGetCurrentChatConfigBase = createQueryHookNoParams<ChatConfigResponse>({
  queryKey: () => chatConfigKeys.current(),
  queryFn: (apiClient) =>
    apiClient.getCurrentConfig().then(response => {
      log.api.response('GET', '/api/admin/chat-config/current', 200, { model: response.data.model });
      return response.data;
    }),
  logContext: 'GET /api/admin/chat-config/current',
  staleTime: QUERY_STALE_TIME.MEDIUM, // Current config may change
});

export function useGetCurrentChatConfig() {
  const { can } = usePermissions();
  return useGetCurrentChatConfigBase({
    enabled: can.configureChatModels
  });
}

/**
 * Hook to get available chat providers.
 * Only enabled for users with chat model configuration permission.
 *
 * Features:
 * - VERY_LONG stale time (30m) as providers change very rarely
 * - Conditional query enabled based on permissions
 * - Automatic request/response/error logging via logContext
 *
 * Usage:
 * @example
 * const { data: providers } = useGetChatProviders();
 */
const useGetChatProvidersBase = createQueryHookNoParams<ChatProviderResponse[]>({
  queryKey: () => chatConfigKeys.providers(),
  queryFn: (apiClient) =>
    apiClient.getAvailableProvidersApiAdminChatConfigProvidersGet().then(response => {
      log.api.response('GET', '/api/admin/chat-config/providers', 200, { count: response.data.length });
      return response.data;
    }),
  logContext: 'GET /api/admin/chat-config/providers',
  staleTime: QUERY_STALE_TIME.VERY_LONG, // Providers change very rarely
});

export function useGetChatProviders() {
  const { can } = usePermissions();
  return useGetChatProvidersBase({
    enabled: can.configureChatModels
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to save/update chat configuration.
 * Handles both creating new configs and updating existing ones.
 */
export const useSaveChatConfig = createMutationHook<StatusResponse, Partial<ChatConfigResponse>>({
  mutationFn: (apiClient, config) => {
    // Check if this is an existing database config
    const isExistingDbConfig = config.id && config.id !== "env-default" && config.id !== "";

    if (isExistingDbConfig) {
      log.api.request('PUT', `/api/admin/chat-config/${config.id}`, {
        configId: config.id,
        model: config.model
      });
      return apiClient.updateConfig(
        config.id!, // Safe to assert - checked by isExistingDbConfig
        config as ChatConfigRequest
      ).then(response => {
        log.api.response('PUT', `/api/admin/chat-config/${config.id}`, 200, { configId: config.id });
        return response.data;
      });
    } else {
      log.api.request('POST', '/api/admin/chat-config/save', {
        model: config.model,
        provider: config.provider_type
      });
      return apiClient.saveConfig(
        config as ChatConfigRequest
      ).then(response => {
        log.api.response('POST', '/api/admin/chat-config/save', 200, { model: config.model });
        return response.data;
      });
    }
  },
  invalidateQueries: [
    chatConfigKeys.configs(),
    chatConfigKeys.current(),
  ],
  logContext: 'POST/PUT /api/admin/chat-config',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('chat_config_saved', 'ChatConfig');
  },
});

/**
 * Hook to delete chat configuration.
 */
export const useDeleteChatConfig = createMutationHook<StatusResponse, string>({
  mutationFn: (apiClient, configId) => {
    log.api.request('DELETE', `/api/admin/chat-config/${configId}`, { configId });
    return apiClient.deleteConfig(configId).then(response => {
      log.api.response('DELETE', `/api/admin/chat-config/${configId}`, 200, { configId });
      return response.data;
    });
  },
  invalidateQueries: [
    chatConfigKeys.configs(),
    chatConfigKeys.current(),
  ],
  logContext: 'DELETE /api/admin/chat-config/:id',
  logMethod: 'DELETE',
  onSuccess: () => {
    log.user.action('chat_config_deleted', 'ChatConfig');
  },
});

/**
 * Hook to set chat configuration as default.
 * Handles both existing and new configs.
 */
export const useSetDefaultChatConfig = createMutationHook<StatusResponse, ChatConfigResponse>({
  mutationFn: (apiClient, config) => {
    // Create a copy with is_default set to true
    const updatedConfig = { ...config, is_default: true };

    // Check if this is an existing database config
    const isExistingDbConfig = config.id && config.id !== "env-default" && config.id !== "";

    if (isExistingDbConfig) {
      log.api.request('PUT', `/api/admin/chat-config/${config.id}`, {
        configId: config.id,
        model: config.model,
        action: 'set_default'
      });
      return apiClient.updateConfig(
        config.id!, // Safe to assert - checked by isExistingDbConfig
        updatedConfig as ChatConfigRequest
      ).then(response => {
        log.api.response('PUT', `/api/admin/chat-config/${config.id}`, 200, { configId: config.id, model: config.model });
        return response.data;
      });
    } else {
      log.api.request('POST', '/api/admin/chat-config/save', {
        model: config.model,
        provider: config.provider_type,
        action: 'set_default'
      });
      return apiClient.saveConfig(
        updatedConfig as ChatConfigRequest
      ).then(response => {
        log.api.response('POST', '/api/admin/chat-config/save', 200, { model: config.model });
        return response.data;
      });
    }
  },
  invalidateQueries: [
    chatConfigKeys.configs(),
    chatConfigKeys.current(),
  ],
  logContext: 'PUT/POST /api/admin/chat-config (set default)',
  logMethod: 'PUT',
  onSuccess: (_data, config) => {
    log.user.action('chat_config_set_default', 'ChatConfig', { model: config.model });
  },
});

/**
 * Hook to test chat configuration.
 * Does not invalidate queries - this is a test operation only.
 */
export const useTestChatConfig = createMutationHook<unknown, Partial<ChatConfigResponse>>({
  mutationFn: (apiClient, config) => {
    log.api.request('POST', '/api/admin/chat-config/test', {
      model: config.model,
      provider: config.provider_type
    });
    return apiClient.testConfig(
      config as ChatConfigRequest
    ).then(response => {
      log.api.response('POST', '/api/admin/chat-config/test', 200, {
        success: response.data.success,
        model: config.model
      });
      return response.data;
    });
  },
  // No query invalidation - this is a test operation
  logContext: 'POST /api/admin/chat-config/test',
  logMethod: 'POST',
});

/**
 * Hook to reset to environment configuration.
 */
export const useResetToEnvConfig = createMutationHook<StatusResponse, void>({
  mutationFn: (apiClient) => {
    log.api.request('POST', '/api/admin/chat-config/reset-to-env');
    return apiClient.resetToEnv().then(response => {
      log.api.response('POST', '/api/admin/chat-config/reset-to-env', 200, {});
      return response.data;
    });
  },
  invalidateQueries: [
    chatConfigKeys.configs(),
    chatConfigKeys.current(),
  ],
  logContext: 'POST /api/admin/chat-config/reset-to-env',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('chat_config_reset_to_env', 'ChatConfig');
  },
});